import tokens from '@kluein/design-tokens';
import { Box, Button, Text, TextInput } from '@kluein/klue-ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAskKlueLog } from 'hooks/dashboard/use-ask-klue-log';
import { AnalyticsAction } from 'lib/analytics';
import { useAskKlueFeedback } from 'pages/dashboard/hooks';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import type { AskKlueModalFeedbackProps } from './AskKlueModalFeedback.types';

const MotionBox = motion(Box);

function AskKlueModalFeedback(props: AskKlueModalFeedbackProps) {
  const { children, onCloseDialog, showDialog = false } = props;
  const { t } = useTranslation(['Dashboard', 'Common']);
  const [feedback, setFeedback] = useState<'yes' | 'no' | undefined>(undefined);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [showFeedbackText, setShowFeedbackText] = useState<boolean>(false);
  const logAskKlueEvent = useAskKlueLog();
  const submitFeedback = useAskKlueFeedback({
    isAskKlueFeedbackDialog: true,
  });

  const handleClickSkip = () => {
    logAskKlueEvent({
      askKlueId: '',
      eventType: AnalyticsAction.skipAskKlueFeedback,
      isAskKlueFeedbackDialog: true,
    });
    onCloseDialog();
  };

  const handleClickYes = () => {
    submitFeedback({ isHelpful: true });
    setFeedback('yes');
    setShowFeedbackText(false);
    onCloseDialog();
  };

  const handleSubmit = useCallback(() => {
    submitFeedback({ isHelpful: false, comment: feedbackText });
    onCloseDialog();
  }, [feedbackText, onCloseDialog, submitFeedback]);

  if (!showDialog) return children;

  return (
    <Box style={{ position: 'relative' }}>
      <Box
        style={{ position: 'absolute', zIndex: 2 }}
        background="rgba(55, 47, 120, 0.20)"
        width="100%"
        height="100%"
      >
        <Box
          style={{ position: 'absolute', right: 0 }}
          background={tokens.color.neutral[100]}
          margin={{ top: 'xsmall', right: 'xsmall' }}
          round={tokens.borderRadius.large}
          pad={{ horizontal: 'small', vertical: 'medium' }}
        >
          <Box direction="row" align="center" gap="small">
            <Text
              color={tokens.color.neutral[900]}
              fontFamily={tokens.fontFamily.Proxima}
              size={tokens.fontSize.headingFont.small}
              fontWeight="semibold"
              lineHeight={tokens.lineHeight.dense}
            >
              {t('Dashboard:askKlue.answerModal.copyFeedbackYesNo')}
            </Text>
            <Box direction="row" align="center" gap="xxsmall">
              <Button
                onClick={() => {
                  setFeedback('no');
                  setShowFeedbackText(true);
                }}
                active={feedback === 'no'}
                variant="secondary"
                size="medium"
                label={t('Common:no')}
                data-tracking-id={TRACKING_IDS.askKlue.feedback.noButton}
              />
              <Button
                onClick={handleClickYes}
                active={feedback === 'yes'}
                variant="secondary"
                size="medium"
                label={t('Common:yes')}
                data-tracking-id={TRACKING_IDS.askKlue.feedback.yesButton}
              />
              <Button
                onClick={handleClickSkip}
                variant="flat-outlined"
                size="medium"
                label={t('Common:skip')}
                data-tracking-id={TRACKING_IDS.askKlue.feedback.skipButton}
              />
            </Box>
          </Box>
          {showFeedbackText && (
            <AnimatePresence>
              <MotionBox
                pad={{ top: 'large' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box>
                  <Box pad={{ bottom: 'xxsmall' }}>
                    <Text
                      color={tokens.color.neutral[900]}
                      size={tokens.fontSize.bodyFont.small}
                      fontWeight="medium"
                    >
                      {t('Dashboard:askKlue.answerModal.feedbackLabel')}
                    </Text>
                  </Box>
                  <TextInput
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    size="small"
                    title={t(
                      'Dashboard:askKlue.answerModal.feedbackPlaceholder',
                    )}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    value={feedbackText}
                    placeholder={t(
                      'Dashboard:askKlue.answerModal.feedbackPlaceholder',
                    )}
                    data-test-id={TEST_IDS.askKlue.feedback.textInput}
                  />
                </Box>
                <Box
                  direction="row"
                  gap="xxsmall"
                  justify="end"
                  pad={{ top: 'medium' }}
                >
                  <Button
                    variant="flat"
                    size="small"
                    label={t('Common:dismiss')}
                    onClick={handleSubmit}
                    data-tracking-id={
                      TRACKING_IDS.askKlue.feedback.dismissButton
                    }
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    label={t('Common:actions.submit')}
                    onClick={handleSubmit}
                    data-tracking-id={
                      TRACKING_IDS.askKlue.feedback.submitButton
                    }
                  />
                </Box>
              </MotionBox>
            </AnimatePresence>
          )}
        </Box>
      </Box>
      {children}
    </Box>
  );
}

export default AskKlueModalFeedback;
